import React, { useState, useEffect } from 'react';
import { dataUtilitiesAPI } from '../services/api';
import { theme } from '../styles/theme';

interface Utility {
  id: string;
  name: string;
  description: string;
  required_config: string[];
  optional_config: string[];
}

interface Job {
  id: string;
  utility_type: string;
  utility_type_display: string;
  status: string;
  status_display: string;
  input_filename: string;
  output_filename: string;
  config: unknown;
  row_count: number;
  column_count: number;
  columns: string[];
  result_summary: unknown;
  error_message: string;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
}

type Step = 'upload' | 'configure' | 'processing' | 'completed';

export default function DataUtilities() {
  const [utilities, setUtilities] = useState<Utility[]>([]);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [currentStep, setCurrentStep] = useState<Step>('upload');
  
  // Upload step
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedUtility, setSelectedUtility] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState(false);
  
  // Configure step
  const [currentJob, setCurrentJob] = useState<Job | null>(null);
  const [config, setConfig] = useState<any>({});
  
  // Preview data
  const [previewData, setPreviewData] = useState<{
    columns: string[];
    rows: string[][];
    total_rows: number;
    preview_rows: number;
  } | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Processing step
  const [pollingInterval, setPollingInterval] = useState<number | null>(null);
  
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadUtilities();
    loadJobs();
  }, []);

  useEffect(() => {
    // Cleanup polling on unmount
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const loadUtilities = async () => {
    try {
      const response = await dataUtilitiesAPI.utilities();
      setUtilities(response.data.utilities);
    } catch (err: unknown) {
      setError('Failed to load utilities');
    }
  };

  const loadJobs = async () => {
    try {
      const response = await dataUtilitiesAPI.list();
      // Handle both paginated and non-paginated responses
      const jobsData = response.data.results || response.data;
      setJobs(Array.isArray(jobsData) ? jobsData : []);
    } catch (err: unknown) {
      setError('Failed to load jobs');
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
      setError('');
    }
  };

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setSelectedFile(e.dataTransfer.files[0]);
      setError('');
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedUtility) {
      setError('Please select both a file and a utility type');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const response = await dataUtilitiesAPI.upload(
        selectedFile,
        selectedUtility,
        (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          }
        }
      );

      setCurrentJob(response.data);
      
      // Load preview data
      try {
        const previewResponse = await dataUtilitiesAPI.preview(response.data.id);
        setPreviewData(previewResponse.data);
        setShowPreview(true);
      } catch (previewErr) {
        console.error('Failed to load preview:', previewErr);
        // Continue without preview if it fails
      }
      
      setCurrentStep('configure');
      setUploadProgress(0);
      loadJobs();
    } catch (err: unknown) {
      const errorMessage = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      setError(errorMessage || 'Failed to upload file');
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfigChange = (field: string, value: unknown) => {
    setConfig((prev: Record<string, unknown>) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleExecute = async () => {
    if (!currentJob) return;

    setLoading(true);
    setError('');

    try {
      // Validate numeric columns for utilities that require it
      const numericUtilities = ['sumif', 'consumption_sumif', 'maxif_numbers'];
      
      if (numericUtilities.includes(currentJob.utility_type)) {
        // Determine which columns need validation
        let columnsToValidate: string[] = [];
        let columnNames: Record<string, string> = {};
        
        if (currentJob.utility_type === 'sumif') {
          columnsToValidate = [config.units_column];
          columnNames[config.units_column] = 'Units Column';
        } else if (currentJob.utility_type === 'consumption_sumif') {
          const yearColumns = config.year_columns || {};
          columnsToValidate = (Object.values(yearColumns) as string[]).filter((col: string) => col !== config.identifier_column);
          Object.entries(yearColumns).forEach(([year, col]) => {
            columnNames[col as string] = `${year} Column`;
          });
        } else if (currentJob.utility_type === 'maxif_numbers') {
          columnsToValidate = [config.value_column];
          columnNames[config.value_column] = 'Value Column';
        }
        
        // Show validation progress
        setError('Validating data...');
        
        const validationResponse = await dataUtilitiesAPI.validate(
          currentJob.id,
          columnsToValidate,
          columnNames
        );
        
        if (!validationResponse.data.valid) {
          const errors = validationResponse.data.errors.slice(0, 5);
          const errorList = errors.map((err: { row?: number; column?: string; message: string }) => 
            `• Row ${err.row}, Column ${err.column}: ${err.message}`
          ).join('\n');
          const total = validationResponse.data.total_errors;
          const moreText = total > 5 ? `\n... and ${total - 5} more errors` : '';
          
          setError(
            `Data validation failed! Found ${total} non-numeric value(s):\n\n${errorList}${moreText}\n\nPlease fix the data in your Excel file and upload again.`
          );
          setLoading(false);
          return;
        }
        
        setError('');
      }
      
      await dataUtilitiesAPI.execute(currentJob.id, config);
      setCurrentStep('processing');
      startPolling(currentJob.id);
    } catch (err: unknown) {
      const errorMessage = err && typeof err === 'object' && 'response' in err
        ? (err as { response?: { data?: { error?: string } } }).response?.data?.error
        : undefined;
      setError(errorMessage || 'Failed to start processing');
      setLoading(false);
    }
  };

  const startPolling = (jobId: string) => {
    const interval = setInterval(async () => {
      try {
        const response = await dataUtilitiesAPI.get(jobId);
        const job: Job = response.data;
        
        setCurrentJob(job);
        
        if (job.status === 'completed') {
          clearInterval(interval);
          setPollingInterval(null);
          setCurrentStep('completed');
          setLoading(false);
          loadJobs();
        } else if (job.status === 'failed') {
          clearInterval(interval);
          setPollingInterval(null);
          setError(job.error_message || 'Processing failed');
          setLoading(false);
          loadJobs();
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000);

    setPollingInterval(interval);
  };

  const handleDownload = async (jobId: string) => {
    try {
      const response = await dataUtilitiesAPI.download(jobId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from job
      const job = jobs.find(j => j.id === jobId) || currentJob;
      link.setAttribute('download', job?.output_filename || 'result.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setError('Failed to download file');
    }
  };

  const handleReset = () => {
    setCurrentStep('upload');
    setSelectedFile(null);
    setSelectedUtility('');
    setCurrentJob(null);
    setConfig({});
    setError('');
    if (pollingInterval) {
      clearInterval(pollingInterval);
      setPollingInterval(null);
    }
  };

  const renderUploadStep = () => (
    <div className="space-y-6">
      <div>
        <h2 className={`text-2xl font-bold ${theme.text.primary} mb-2`}>
          Upload Data File
        </h2>
      </div>

      {/* File Upload */}
      <div
        className={`border-2 border-dashed ${theme.border.secondary} rounded-lg p-8 text-center hover:border-interactive-primary transition-colors`}
        onDrop={handleFileDrop}
        onDragOver={(e) => e.preventDefault()}
      >
        {selectedFile ? (
          <div>
            <div className="text-qc-success mb-2">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className={`${theme.text.primary} font-semibold`}>{selectedFile.name}</p>
            <p className="text-sm text-text-tertiary">
              {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
            </p>
            <button
              onClick={() => setSelectedFile(null)}
              className="mt-2 text-sm text-qc-error hover:text-qc-error"
            >
              Remove file
            </button>
          </div>
        ) : (
          <div>
            <svg className="w-12 h-12 mx-auto text-text-tertiary mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <p className={`${theme.text.secondary} mb-2`}>
              Drag and drop your file here, or
            </p>
            <label className="inline-block px-4 py-2 bg-interactive-primary text-text-inverse rounded-lg cursor-pointer hover:bg-interactive-primary-hover transition-colors">
              Browse Files
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileSelect}
                className="hidden"
              />
            </label>
            <p className="text-xs text-text-tertiary mt-2">
              Supported formats: Excel (.xlsx, .xls), CSV (.csv)
            </p>
          </div>
        )}
      </div>

      {/* Utility Selection */}
      <div>
        <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
          Select Data Processing Utility
        </label>
        <select
          value={selectedUtility}
          onChange={(e) => setSelectedUtility(e.target.value)}
          className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary} focus:ring-2 focus:ring-interactive-primary`}
        >
          <option value="">-- Choose a utility --</option>
          {utilities.map((utility) => (
            <option key={utility.id} value={utility.id}>
              {utility.name}
            </option>
          ))}
        </select>

        {selectedUtility && (
          <div className={`mt-3 p-4 ${theme.status.info.background} rounded-lg`}>
            <p className="text-sm text-interactive-primary">
              <strong>Description:</strong>{' '}
              {utilities.find((u) => u.id === selectedUtility)?.description}
            </p>
          </div>
        )}
      </div>

      {/* Upload Progress */}
      {isUploading && (
        <div>
          <div className={`flex justify-between text-sm ${theme.text.secondary} mb-2`}>
            <span>Uploading...</span>
            <span>{uploadProgress}%</span>
          </div>
          <div className="w-full bg-bg-hover rounded-full h-2">
            <div
              className="bg-interactive-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${uploadProgress}%` }}
            />
          </div>
        </div>
      )}

      {/* Upload Button */}
      <button
        onClick={handleUpload}
        disabled={!selectedFile || !selectedUtility || isUploading}
        className="w-full px-6 py-3 bg-interactive-primary text-text-inverse rounded-lg hover:bg-interactive-primary-hover disabled:bg-bg-subtle disabled:text-text-tertiary disabled:cursor-not-allowed transition-colors font-semibold"
      >
        {isUploading ? 'Uploading...' : 'Upload and Continue'}
      </button>
    </div>
  );

  const renderConfigureStep = () => {
    if (!currentJob) return null;

    const utility = utilities.find((u) => u.id === currentJob.utility_type);
    if (!utility) return null;

    return (
      <div className="space-y-6">
        <div>
          <h2 className={`text-2xl font-bold ${theme.text.primary} mb-2`}>
            Configure {utility.name}
          </h2>
          <p className={`${theme.text.secondary}`}>
            Select the columns and configure options for processing
          </p>
        </div>

        {/* File Info */}
        <div className="bg-bg-subtle p-4 rounded-lg">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className={`${theme.text.secondary}`}>File:</span>
              <p className={`font-semibold ${theme.text.primary}`}>{currentJob.input_filename}</p>
            </div>
            <div>
              <span className={`${theme.text.secondary}`}>Rows:</span>
              <p className={`font-semibold ${theme.text.primary}`}>{currentJob.row_count.toLocaleString()}</p>
            </div>
            <div className="col-span-2">
              <span className={`${theme.text.secondary}`}>Columns ({currentJob.column_count}):</span>
              <div className="flex flex-wrap gap-2 mt-2">
                {currentJob.columns.slice(0, 10).map((col, idx) => (
                  <span key={idx} className="px-2 py-1 bg-bg-surface text-xs rounded border border-border">
                    {col}
                  </span>
                ))}
                {currentJob.columns.length > 10 && (
                  <span className="px-2 py-1 text-xs text-text-tertiary">
                    +{currentJob.columns.length - 10} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Data Preview */}
        {showPreview && previewData && (
          <div className={`${theme.card.background} border border-border rounded-lg overflow-hidden`}>
            <div className="bg-bg-subtle px-4 py-3 border-b border-border flex items-center justify-between">
              <div>
                <h3 className={`text-lg font-semibold ${theme.text.primary}`}>
                  Data Preview
                </h3>
                <p className={`text-sm ${theme.text.secondary}`}>
                  Showing first {previewData.preview_rows} of {previewData.total_rows.toLocaleString()} rows
                </p>
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="text-text-tertiary hover:text-text-secondary"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-auto max-h-96">
              <table className={`min-w-full divide-y ${theme.border.primary.replace('border-', 'divide-')}`}>
                <thead className="bg-bg-subtle sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider border-r border-border">
                      #
                    </th>
                    {previewData.columns.map((col, idx) => (
                      <th
                        key={idx}
                        className="px-4 py-2 text-left text-xs font-medium text-text-tertiary uppercase tracking-wider border-r border-border"
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className={`${theme.card.background} divide-y divide-border`}>
                  {previewData.rows.map((row, rowIdx) => (
                    <tr key={rowIdx} className="hover:bg-bg-subtle">
                      <td className="px-4 py-2 text-sm text-text-tertiary border-r border-border font-medium">
                        {rowIdx + 1}
                      </td>
                      {row.map((cell, cellIdx) => (
                        <td
                          key={cellIdx}
                          className="px-4 py-2 text-sm text-text-primary border-r border-border whitespace-nowrap"
                        >
                          {cell || <span className="text-text-tertiary">—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-bg-subtle px-4 py-2 border-t border-border">
              <button
                onClick={() => setShowPreview(false)}
                className="text-sm text-interactive-primary hover:underline"
              >
                Hide Preview
              </button>
            </div>
          </div>
        )}
        
        {!showPreview && previewData && (
          <button
            onClick={() => setShowPreview(true)}
            className={`w-full px-4 py-2 border border-border ${theme.text.secondary} rounded-lg hover:bg-bg-subtle transition-colors text-sm font-medium`}
          >
            Show Data Preview ({previewData.total_rows.toLocaleString()} rows)
          </button>
        )}

        {/* Dynamic Configuration Form */}
        <div className="space-y-4">
          {renderConfigFields(utility, currentJob)}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3">
          <button
            onClick={handleReset}
            className={`flex-1 px-6 py-3 border border-border ${theme.text.secondary} rounded-lg hover:bg-bg-subtle transition-colors font-semibold`}
          >
            Cancel
          </button>
          <button
            onClick={handleExecute}
            disabled={loading}
            className="flex-1 px-6 py-3 bg-qc-success text-text-inverse rounded-lg hover:bg-qc-success/90 disabled:bg-bg-subtle disabled:text-text-tertiary disabled:cursor-not-allowed transition-colors font-semibold"
          >
            {loading ? 'Starting...' : 'Process Data'}
          </button>
        </div>
      </div>
    );
  };

  const renderConfigFields = (utility: Utility, job: Job) => {
    const { id } = utility;

    switch (id) {
      case 'sumif':
        return (
          <>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Identifier Column <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <select
                value={config.identifier_column || ''}
                onChange={(e) => handleConfigChange('identifier_column', e.target.value)}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
              >
                <option value="">-- Select column --</option>
                {job.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Units Column <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <select
                value={config.units_column || ''}
                onChange={(e) => handleConfigChange('units_column', e.target.value)}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
              >
                <option value="">-- Select column --</option>
                {job.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
          </>
        );

      case 'consumption_sumif':
        return (
          <>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Identifier Column <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <select
                value={config.identifier_column || ''}
                onChange={(e) => handleConfigChange('identifier_column', e.target.value)}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
              >
                <option value="">-- Select column --</option>
                {job.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Year Columns <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <div className="space-y-2">
                {[2021, 2022, 2023, 2024].map((year) => (
                  <div key={year} className="flex items-center gap-2">
                    <span className={`w-16 text-sm ${theme.text.secondary}`}>{year}:</span>
                    <select
                      value={config.year_columns?.[year] || ''}
                      onChange={(e) => {
                        const newYearColumns = { ...(config.year_columns || {}), [year]: e.target.value };
                        handleConfigChange('year_columns', newYearColumns);
                      }}
                      className={`flex-1 px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                    >
                      <option value="">-- Skip this year --</option>
                      {job.columns.map((col) => (
                        <option key={col} value={col}>{col}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </>
        );

      case 'maxifs':
        return (
          <>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Identifier Column <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <select
                value={config.identifier_column || ''}
                onChange={(e) => handleConfigChange('identifier_column', e.target.value)}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
              >
                <option value="">-- Select column --</option>
                {job.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Data Columns to Check for Completeness <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <select
                multiple
                value={config.data_columns || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                  handleConfigChange('data_columns', selected);
                }}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary} min-h-[200px]`}
              >
                {job.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
              <p className="text-xs text-text-tertiary mt-1">
                Hold Ctrl (Cmd on Mac) to select multiple columns
              </p>
            </div>
          </>
        );

      case 'maxif_numbers':
        return (
          <>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Identifier Column <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <select
                value={config.identifier_column || ''}
                onChange={(e) => handleConfigChange('identifier_column', e.target.value)}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
              >
                <option value="">-- Select column --</option>
                {job.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Value Column (to find maximum) <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <select
                value={config.value_column || ''}
                onChange={(e) => handleConfigChange('value_column', e.target.value)}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
              >
                <option value="">-- Select column --</option>
                {job.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
          </>
        );

      case 'excel_cleansing':
        return (
          <div>
            <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
              Columns to Convert (European Currency Format) <span className="text-text-tertiary" aria-hidden="true">*</span>
            </label>
            <select
              multiple
              value={config.columns_to_convert || []}
              onChange={(e) => {
                const selected = Array.from(e.target.selectedOptions, option => option.value);
                handleConfigChange('columns_to_convert', selected);
              }}
              className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary} min-h-[200px]`}
            >
              {job.columns.map((col) => (
                <option key={col} value={col}>{col}</option>
              ))}
            </select>
            <p className="text-xs text-text-tertiary mt-1">
              Hold Ctrl (Cmd on Mac) to select multiple columns. Converts formats like "2.300,90" to 2300.90
            </p>
          </div>
        );

      case 'combining_multiple':
        return (
          <>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Identifier Column <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <select
                value={config.identifier_column || ''}
                onChange={(e) => handleConfigChange('identifier_column', e.target.value)}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
              >
                <option value="">-- Select column --</option>
                {job.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Columns to Combine <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <select
                multiple
                value={config.columns_to_combine || []}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions, option => option.value);
                  handleConfigChange('columns_to_combine', selected);
                }}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary} min-h-[150px]`}
              >
                {job.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
              <p className="text-xs text-text-tertiary mt-1">
                Hold Ctrl (Cmd on Mac) to select multiple columns
              </p>
            </div>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Separator
              </label>
              <input
                type="text"
                value={config.separator || ', '}
                onChange={(e) => handleConfigChange('separator', e.target.value)}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                placeholder=", "
              />
            </div>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={config.remove_duplicates ?? true}
                onChange={(e) => handleConfigChange('remove_duplicates', e.target.checked)}
                className="w-4 h-4 text-interactive-primary rounded"
              />
              <label className={`ml-2 text-sm ${theme.text.secondary}`}>
                Remove duplicate values
              </label>
            </div>
          </>
        );

      case 'finding_similar':
        return (
          <>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Customer Column (in uploaded file) <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <select
                value={config.customer_column || ''}
                onChange={(e) => handleConfigChange('customer_column', e.target.value)}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
              >
                <option value="">-- Select column --</option>
                {job.columns.map((col) => (
                  <option key={col} value={col}>{col}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Database File Path <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <input
                type="text"
                value={config.database_file_path || ''}
                onChange={(e) => handleConfigChange('database_file_path', e.target.value)}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                placeholder="Path to database Excel file"
              />
              <p className="text-xs text-text-tertiary mt-1">
                Full path to your internal database file
              </p>
            </div>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Database Column Name <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <input
                type="text"
                value={config.database_column || ''}
                onChange={(e) => handleConfigChange('database_column', e.target.value)}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                placeholder="Column name in database file"
              />
            </div>
            <div>
              <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                Database Part Number Column <span className="text-text-tertiary" aria-hidden="true">*</span>
              </label>
              <input
                type="text"
                value={config.database_part_number_column || ''}
                onChange={(e) => handleConfigChange('database_part_number_column', e.target.value)}
                className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                placeholder="Part number column in database"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                  Minimum Match % (Optional)
                </label>
                <input
                  type="number"
                  value={config.min_percentage || 50}
                  onChange={(e) => handleConfigChange('min_percentage', parseFloat(e.target.value))}
                  className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                  min="0"
                  max="100"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${theme.text.secondary} mb-2`}>
                  Max Matches (Optional)
                </label>
                <input
                  type="number"
                  value={config.max_matches || 4}
                  onChange={(e) => handleConfigChange('max_matches', parseInt(e.target.value))}
                  className={`w-full px-4 py-2 border border-border rounded-lg bg-bg-surface ${theme.text.primary}`}
                  min="1"
                  max="20"
                />
              </div>
            </div>
          </>
        );

      default:
        return <p className="text-text-tertiary">Configuration options not available for this utility.</p>;
    }
  };

  const renderProcessingStep = () => (
    <div className="space-y-6 text-center">
      <div>
        <h2 className={`text-2xl font-bold ${theme.text.primary} mb-2`}>
          Processing Data
        </h2>
        <p className={`${theme.text.secondary}`}>
          Please wait while we process your file...
        </p>
      </div>

      <div className="flex justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-interactive-primary"></div>
      </div>

      {currentJob && (
        <div className="bg-bg-subtle p-4 rounded-lg text-left max-w-md mx-auto">
          <p className={`text-sm ${theme.text.secondary}`}>
            <strong>File:</strong> {currentJob.input_filename}
          </p>
          <p className={`text-sm ${theme.text.secondary}`}>
            <strong>Utility:</strong> {currentJob.utility_type_display}
          </p>
          <p className={`text-sm ${theme.text.secondary}`}>
            <strong>Status:</strong> <span className="text-interactive-primary">{currentJob.status_display}</span>
          </p>
        </div>
      )}
    </div>
  );

  const renderCompletedStep = () => (
    <div className="space-y-6">
      <div className="text-center">
        <div className="text-qc-success mb-4">
          <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className={`text-2xl font-bold ${theme.text.primary} mb-2`}>
          Processing Complete!
        </h2>
        <p className={`${theme.text.secondary}`}>
          Your data has been processed successfully
        </p>
      </div>

      {currentJob && (
        <>
          {/* Results Summary */}
          <div className="bg-bg-subtle p-6 rounded-lg">
            <h3 className={`font-semibold ${theme.text.primary} mb-4`}>Results Summary</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {Object.entries(currentJob.result_summary || {}).map(([key, value]) => (
                <div key={key}>
                  <span className={`${theme.text.secondary} capitalize`}>
                    {key.replace(/_/g, ' ')}:
                  </span>
                  <p className={`font-semibold ${theme.text.primary}`}>
                    {typeof value === 'number' ? value.toLocaleString() : String(value)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={() => handleDownload(currentJob.id)}
              className="flex-1 px-6 py-3 bg-qc-success text-text-inverse rounded-lg hover:bg-qc-success/90 transition-colors font-semibold flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download Results
            </button>
            <button
              onClick={handleReset}
              className={`flex-1 px-6 py-3 border border-border ${theme.text.secondary} rounded-lg hover:bg-bg-subtle transition-colors font-semibold`}
            >
              Process Another File
            </button>
          </div>
        </>
      )}
    </div>
  );

  const renderPreviousJobs = () => (
    <div className="mt-8">
      <h3 className={`text-xl font-bold ${theme.text.primary} mb-4`}>
        Previous Jobs
      </h3>
      
      {!Array.isArray(jobs) || jobs.length === 0 ? (
        <p className="text-text-tertiary text-center py-8">
          No previous jobs found
        </p>
      ) : (
        <div className="space-y-3">
          {jobs.slice(0, 10).map((job) => (
            <div
              key={job.id}
              className={`${theme.card.background} border border-border rounded-lg p-4 hover:shadow-md transition-shadow`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className={`font-semibold ${theme.text.primary}`}>
                      {job.input_filename}
                    </h4>
                    <span
                      className={`px-2 py-1 text-xs rounded ${
                        job.status === 'completed'
                          ? 'bg-qc-success/10 text-qc-success'
                          : job.status === 'failed'
                          ? 'bg-qc-error/10 text-qc-error'
                          : job.status === 'processing'
                          ? 'bg-interactive-primary/10 text-interactive-primary'
                          : 'bg-bg-subtle text-text-primary'
                      }`}
                    >
                      {job.status_display}
                    </span>
                  </div>
                  <div className={`flex items-center gap-4 text-sm ${theme.text.secondary}`}>
                    <span>{job.utility_type_display}</span>
                    <span>•</span>
                    <span>{new Date(job.created_at).toLocaleString()}</span>
                    {job.row_count > 0 && (
                      <>
                        <span>•</span>
                        <span>{job.row_count.toLocaleString()} rows</span>
                      </>
                    )}
                  </div>
                </div>
                
                {job.status === 'completed' && (
                  <button
                    onClick={() => handleDownload(job.id)}
                    className="ml-4 px-4 py-2 bg-interactive-primary text-text-inverse rounded-lg hover:bg-interactive-primary-hover transition-colors text-sm font-semibold"
                  >
                    Download
                  </button>
                )}
                
                {job.status === 'failed' && job.error_message && (
                  <div className="ml-4 text-xs text-qc-error max-w-xs">
                    {job.error_message}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h2 className={`text-3xl font-bold ${theme.text.primary} mb-2`}>
            Data Processing Utilities
          </h2>
          <p className={`${theme.text.secondary}`}>
            Upload your data files and apply powerful processing utilities like SUMIF, MAXIFS, cleansing, and more
          </p>
        </div>

        {/* Error Message */}
        {error && (
          <div className={`mb-6 p-4 ${theme.status.error.background} border border-qc-error/30 rounded-lg`}>
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-qc-error flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                {error.includes('Validating') ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-interactive-primary"></div>
                    <p className="text-interactive-primary font-medium">{error}</p>
                  </div>
                ) : (
                  <pre className="text-qc-error whitespace-pre-wrap font-sans text-sm">{error}</pre>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            {(['upload', 'configure', 'processing', 'completed'] as Step[]).map((step, index) => (
              <React.Fragment key={step}>
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                      currentStep === step
                        ? 'bg-interactive-primary text-text-inverse'
                        : ['upload', 'configure', 'processing', 'completed'].indexOf(currentStep) >
                          ['upload', 'configure', 'processing', 'completed'].indexOf(step)
                        ? 'bg-qc-success text-text-inverse'
                        : 'bg-bg-hover text-text-secondary'
                    }`}
                  >
                    {['upload', 'configure', 'processing', 'completed'].indexOf(currentStep) >
                    ['upload', 'configure', 'processing', 'completed'].indexOf(step) ? (
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span className={`text-xs mt-2 ${theme.text.secondary} capitalize`}>
                    {step}
                  </span>
                </div>
                {index < 3 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      ['upload', 'configure', 'processing', 'completed'].indexOf(currentStep) >
                      index
                        ? 'bg-qc-success'
                        : 'bg-bg-hover'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className={`${theme.card.background} rounded-lg shadow-lg p-8`}>
          {currentStep === 'upload' && renderUploadStep()}
          {currentStep === 'configure' && renderConfigureStep()}
          {currentStep === 'processing' && renderProcessingStep()}
          {currentStep === 'completed' && renderCompletedStep()}
        </div>

        {/* Previous Jobs List */}
        {currentStep === 'upload' && renderPreviousJobs()}
      </div>
    </div>
  );
}
